import { useCallback, useEffect, useState } from 'react'
import { catalogueRepository } from '../../data/repositories/CatalogueRepositoryImpl'
import { CatalogItem } from '../../domain/entities/Catalogue'

export const useCatalogue = (initialLimit = 10) => {
  const [items, setItems] = useState<CatalogItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled'>('all')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const fetchItems = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await catalogueRepository.getCatalogItems({
        search: search || undefined,
        status: statusFilter,
        page,
        limit: initialLimit,
      })
      setItems(res.items)
      setTotalPages(res.totalPages)
      setTotalItems(res.totalItems)
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to load catalogue items.')
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter, page, initialLimit])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  const createItem = async (item: Omit<CatalogItem, 'id' | 'addedBy' | 'isDeleted' | 'createdAt' | 'updatedAt'>) => {
    setError(null)
    try {
      await catalogueRepository.createCatalogItem(item)
      await fetchItems()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to create catalog item.')
      throw err
    }
  }

  const updateItem = async (
    id: string,
    item: Partial<Omit<CatalogItem, 'id' | 'addedBy' | 'isDeleted' | 'createdAt' | 'updatedAt'>>,
  ) => {
    setError(null)
    try {
      await catalogueRepository.updateCatalogItem(id, item)
      await fetchItems()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to update catalog item.')
      throw err
    }
  }

  const deleteItem = async (id: string) => {
    setError(null)
    try {
      await catalogueRepository.deleteCatalogItem(id)
      await fetchItems()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to delete catalog item.')
      throw err
    }
  }

  const restoreItem = async (id: string) => {
    setError(null)
    try {
      await catalogueRepository.restoreCatalogItem(id)
      await fetchItems()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to restore catalog item.')
      throw err
    }
  }

  const resetFilters = () => {
    setSearch('')
    setStatusFilter('all')
    setPage(1)
  }

  return {
    items,
    loading,
    error,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    page,
    setPage,
    totalPages,
    totalItems,
    createItem,
    updateItem,
    deleteItem,
    restoreItem,
    resetFilters,
    refresh: fetchItems,
  }
}
